import React, { useState } from 'react';
import { FaStar, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaUserGraduate, FaShoppingCart } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';

/**
 * Component for displaying detailed school profile information
 */
const SchoolProfile = ({ school }) => {
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [activeTab, setActiveTab] = useState('about');
  const [cart, setCart] = useState([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Render star ratings
  const renderRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar key={i} className={i <= rating ? "text-yellow-500" : "text-gray-300"} />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };
  
  // Add product to cart
  const addToCart = (product) => {
    setCart([...cart, product]);
    showSuccess(`${product.name} added to cart`);
  };
  
  // Handle checkout click
  const handleCheckout = () => {
    if (isAuthenticated) {
      // Navigate to checkout
      window.location.href = '/checkout';
    } else {
      setShowLoginPrompt(true);
    }
  };
  
  return (
    <div>
      {/* School Photos */}
      {school.photos && school.photos.length > 0 ? (
        <div className="mb-6 h-64 bg-gray-100 rounded-lg overflow-hidden">
          <img 
            src={school.photos[0]} 
            alt={school.name} 
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="mb-6 h-64 bg-gray-200 flex items-center justify-center rounded-lg">
          <FaUserGraduate className="text-gray-400 text-4xl" />
        </div>
      )}
      
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" aria-label="School Profile">
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'about' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'facilities' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('facilities')}
          >
            Facilities & Programs
          </button>
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'shop' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('shop')}
          >
            School Shop
          </button>
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'contact' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </nav>
      </div>
      
      {/* About Tab */}
      {activeTab === 'about' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold mb-4">About {school.name}</h3>
            <p className="text-gray-700 mb-6">
              {typeof school.description === 'object' ? JSON.stringify(school.description) : (school.description || "No description available.")}
            </p>
            
            <h4 className="font-semibold mb-3">Key Information</h4>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="flex items-start mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <FaUserGraduate className="text-blue-600 text-xs" />
                  </span>
                  <span>
                    <span className="font-medium">Curriculum:</span>{' '}
                    {Array.isArray(school.curriculum) 
                      ? school.curriculum.join(', ') 
                      : (typeof school.curriculum === 'object' ? JSON.stringify(school.curriculum) : (school.curriculum || 'N/A'))}
                  </span>
                </p>
                <p className="flex items-start mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <FaMapMarkerAlt className="text-blue-600 text-xs" />
                  </span>
                  <span>
                    <span className="font-medium">Location:</span>{' '}
                    {typeof school.location === 'object' && school.location !== null ? (
                      <>
                        {school.location.county || 'N/A'}
                        {school.location.town ? `, ${school.location.town}` : ''}
                      </>
                    ) : (
                      'N/A'
                    )}
                  </span>
                </p>
                <p className="flex items-start mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <FaUserGraduate className="text-blue-600 text-xs" />
                  </span>
                  <span>
                    <span className="font-medium">School Type:</span>{' '}
                    {[
                      school.hasBoarding ? 'Boarding' : null,
                      school.hasDaySchool ? 'Day School' : null
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </p>
              </div>
              <div>
                <p className="flex items-start mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <FaCalendarAlt className="text-blue-600 text-xs" />
                  </span>
                  <span>
                    <span className="font-medium">Founded:</span>{' '}
                    {school.foundedYear || 'N/A'}
                  </span>
                </p>
                <p className="flex items-start mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <FaUsers className="text-blue-600 text-xs" />
                  </span>
                  <span>
                    <span className="font-medium">Student Population:</span>{' '}
                    {school.studentPopulation ? school.studentPopulation.toLocaleString() : 'N/A'}
                  </span>
                </p>
                <p className="flex items-start mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <FaUsers className="text-blue-600 text-xs" />
                  </span>
                  <span>
                    <span className="font-medium">Class Size:</span>{' '}
                    {school.averageClassSize || 'N/A'}
                  </span>
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-3">Ratings & Reviews</h4>
              <div className="flex items-center mb-2">
                {renderRatingStars(school.rating || 0)}
                <span className="ml-2 font-medium">{school.rating || 0}/5</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Based on {school.reviewCount || 0} reviews
              </p>
              
              {isAuthenticated ? (
                <button className="text-sm text-primary font-medium">
                  Write a Review
                </button>
              ) : (
                <div className="text-xs text-primary">
                  <Link to="/login" className="font-medium">Login</Link> to write a review
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-3">Fees</h4>
              {school.fees?.termly ? (
                <div>
                  <p className="text-sm mb-2">
                    <span className="font-medium">Termly:</span>{' '}
                    KES {school.fees.termly.toLocaleString()}
                  </p>
                  {school.fees.annualTotal && (
                    <p className="text-sm mb-2">
                      <span className="font-medium">Annual Total:</span>{' '}
                      KES {school.fees.annualTotal.toLocaleString()}
                    </p>
                  )}
                  {school.fees.admissionFee && (
                    <p className="text-sm mb-2">
                      <span className="font-medium">Admission Fee:</span>{' '}
                      KES {school.fees.admissionFee.toLocaleString()}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Fee information not available</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Facilities & Programs Tab */}
      {activeTab === 'facilities' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Facilities & Programs</h3>
          
          <h4 className="font-semibold text-gray-700 mb-3">Facilities</h4>
          <div className="flex flex-wrap gap-2 mb-6">
            {school.facilities && school.facilities.length > 0 ? (
              school.facilities.map((facility, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full"
                >
                  {facility}
                </span>
              ))
            ) : (
              <p className="text-gray-500">No facility information available</p>
            )}
          </div>
          
          <h4 className="font-semibold text-gray-700 mb-3">Programs & Activities</h4>
          <div className="mb-6">
            {school.programs ? (
              <p className="text-gray-700">{school.programs}</p>
            ) : (
              <p className="text-gray-500">No program information available</p>
            )}
          </div>
        </div>
      )}
      
      {/* Shop Tab */}
      {activeTab === 'shop' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">School Shop</h3>
            
            {cart.length > 0 && (
              <button 
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                onClick={handleCheckout}
              >
                <FaShoppingCart className="mr-2" />
                Checkout ({cart.length})
              </button>
            )}
          </div>
          
          {/* Example Products */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { id: 1, name: 'School Uniform', price: 5000, image: 'https://images.unsplash.com/photo-1604671801908-6f0c6a092c05?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80' },
              { id: 2, name: 'Sports Kit', price: 3500, image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80' },
              { id: 3, name: 'School Bag', price: 2000, image: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80' }
            ].map(product => (
              <div key={product.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="h-40 overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <h4 className="font-medium mb-1">{product.name}</h4>
                  <p className="text-gray-700 mb-3">KES {product.price.toLocaleString()}</p>
                  <button 
                    className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded"
                    onClick={() => addToCart(product)}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center text-gray-500">
            {isAuthenticated ? (
              <p>
                Visit the <Link to={`/shop/${school._id}`} className="text-primary">full school shop</Link> for more products
              </p>
            ) : (
              <p>
                <Link to="/login" className="text-primary">Login</Link> or <Link to="/register" className="text-primary">register</Link> to see more products and shop
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Contact Details</h4>
              <p className="mb-2">
                <span className="font-medium">Email:</span> contact@{school.name.toLowerCase().replace(/\s+/g, '')}example.com
              </p>
              <p className="mb-2">
                <span className="font-medium">Phone:</span> +254 (0)7XX XXX XXX
              </p>
              {school.website && (
                <p className="mb-2">
                  <span className="font-medium">Website:</span>{' '}
                  <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-primary">
                    {school.website.replace(/^https?:\/\//, '')}
                  </a>
                </p>
              )}
              <p className="mb-2">
                <span className="font-medium">Address:</span>{' '}
                {typeof school.location === 'object' && school.location !== null ? (
                  <>
                    {school.location.town ? `${school.location.town}, ` : ''}
                    {school.location.county || 'Location unknown'}
                  </>
                ) : (
                  'Location unknown'
                )}
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Send Enquiry</h4>
              <div className="space-y-3">
                <input type="text" className="form-input w-full" placeholder="Your Name" />
                <input type="email" className="form-input w-full" placeholder="Your Email" />
                <textarea className="form-textarea w-full" rows="3" placeholder="Your Message"></textarea>
                <button className="w-full py-2 bg-primary text-white rounded hover:bg-primary-dark">
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Login Required</h3>
            <p className="mb-4">Please log in or register to complete your purchase.</p>
            <div className="flex justify-end space-x-3">
              <button 
                className="px-4 py-2 border border-gray-300 rounded"
                onClick={() => setShowLoginPrompt(false)}
              >
                Cancel
              </button>
              <Link 
                to="/login" 
                className="px-4 py-2 bg-primary text-white rounded"
              >
                Login
              </Link>
              <Link 
                to="/register" 
                className="px-4 py-2 bg-secondary text-white rounded"
              >
                Register
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolProfile; 